import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Pin, PinOff, ImageOff } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { EnrichedToothImage } from '@/hooks/useToothImages';
import { ImageLightbox } from './ImageLightbox';
import { useDentalOverlay } from './DentalOverlayContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

function groupByDateAndKind(images: EnrichedToothImage[]) {
  const groups = new Map<string, EnrichedToothImage[]>();

  for (const img of images) {
    const date = new Date(img.acquired_at).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const key = `${date}__${img.image_kind}`;
    const existing = groups.get(key) ?? [];
    groups.set(key, [...existing, img]);
  }

  return Array.from(groups.entries()).map(([key, imgs]) => {
    const [date] = key.split('__');
    return { date, kind: imgs[0].image_kind, images: imgs };
  });
}

function kindLabel(kind: string, t: (k: string) => string) {
  return t(`dental.imaging.kinds.${kind}`);
}

// ── Image card ────────────────────────────────────────────────────────────────

interface ImageCardProps {
  image: EnrichedToothImage;
  onClick: () => void;
  isPinned: boolean;
  onPinToggle: () => void;
}

function ImageCard({ image, onClick, isPinned, onPinToggle }: ImageCardProps) {
  const { t } = useTranslation();

  return (
    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-indigo-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
      {/* Thumbnail */}
      <button onClick={onClick} className="block h-36 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {image.signedUrl ? (
          <img
            src={image.signedUrl}
            alt={image.document_original_filename ?? 'tooth image'}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <ImageOff className="h-8 w-8 text-slate-300 dark:text-slate-600" />
          </div>
        )}
      </button>

      {/* Footer */}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <Camera className="h-3.5 w-3.5 flex-shrink-0 text-indigo-400" />
        <div className="min-w-0 flex-1">
          {image.tooth_fdis.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {image.tooth_fdis.map((fdi) => (
                <span
                  key={fdi}
                  className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                >
                  {fdi}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Pin button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPinToggle(); }}
          title={isPinned ? t('dental.imaging.gallery.unpinOverlay') : t('dental.imaging.gallery.pinOverlay')}
          className={cn(
            'rounded-lg p-1 transition-colors',
            isPinned
              ? 'text-indigo-500 hover:text-indigo-700'
              : 'text-slate-300 hover:text-indigo-400 dark:text-slate-600',
          )}
        >
          {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ── Group header ──────────────────────────────────────────────────────────────

function GroupHeader({ date, kind, count, t }: { date: string; kind: string; count: number; t: (k: string) => string }) {
  return (
    <div className="flex items-center gap-2 pb-2 pt-4 first:pt-0">
      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
        {kindLabel(kind, t)} {count > 1 ? `${count}-stk` : ''}
      </span>
      <span className="text-sm text-slate-400 dark:text-slate-500">— {date}</span>
      <div className="flex-1 border-t border-slate-200 dark:border-slate-700" />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface ImageGalleryProps {
  images: EnrichedToothImage[];
  patientId: string;
  userId: string;
  filterToothFdi?: string | null;
}

export function ImageGallery({ images, patientId, userId, filterToothFdi }: ImageGalleryProps) {
  const { t } = useTranslation();
  const { pinnedImage, setPinnedImage, setOpacity } = useDentalOverlay();

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filteredImages = useMemo(
    () =>
      filterToothFdi
        ? images.filter((img) => img.tooth_fdis.includes(filterToothFdi))
        : images,
    [images, filterToothFdi],
  );

  const groups = useMemo(() => groupByDateAndKind(filteredImages), [filteredImages]);

  const handlePinToggle = (img: EnrichedToothImage) => {
    if (pinnedImage?.id === img.id) {
      setPinnedImage(null);
    } else {
      setPinnedImage(img);
      setOpacity(0.5);
    }
  };

  if (filteredImages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 dark:border-slate-700 dark:bg-slate-900/50">
        <div className="text-center">
          <Camera className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
          <p className="mt-2 text-sm text-slate-400 dark:text-slate-500">
            {t('dental.imaging.gallery.empty')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {groups.map((group) => (
        <div key={`${group.date}__${group.kind}`}>
          <GroupHeader date={group.date} kind={group.kind} count={group.images.length} t={t} />
          <div className="grid grid-cols-3 gap-3 xl:grid-cols-4">
            {group.images.map((img) => {
              const globalIdx = filteredImages.indexOf(img);
              return (
                <ImageCard
                  key={img.id}
                  image={img}
                  onClick={() => setLightboxIndex(globalIdx)}
                  isPinned={pinnedImage?.id === img.id}
                  onPinToggle={() => handlePinToggle(img)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={filteredImages}
          initialIndex={lightboxIndex}
          patientId={patientId}
          userId={userId}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
